import React from 'react';
import {useIntl, IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {formatQuoteDate} from '../quoteDocument';
import {parseTermsBlocks} from '../terms';
import PlateToHtml from './PlateToHtml';
import QuotePreviewEstimate from './QuotePreviewEstimate';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';
import * as S from './quotePreviewStyles';

type PageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
};

type FramedPageProps = PageProps & {
	page: number;
	total: number;
	variant?: 'cover';
	children: React.ReactNode;
};

/** The A4 frame every preview page shares — the HTML twin of the PDF's QuotePage. */
const PreviewPage = ({data, template, page, total, variant, children}: FramedPageProps): JSX.Element => {
	const intl = useIntl();
	const logoSrc = data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const runningHead = data.clientName ? `${data.clientName} · ${data.projectName}` : data.projectName;
	return (
		<S.PageSheet $font={template.font} data-cy="quote-preview-page">
			<S.Band $cover={variant === 'cover'}>
				{variant !== 'cover' && (
					<S.BandRun>
						{logoSrc ? (
							<S.BandLogoImage src={logoSrc} crossOrigin="use-credentials" data-private alt="" />
						) : (
							<S.BandLogoWord>{data.companyName}</S.BandLogoWord>
						)}
						<S.BandRunText>{runningHead}</S.BandRunText>
					</S.BandRun>
				)}
				<S.BandConfidential>{intl.formatMessage({id: 'quote_designer.pdf.confidential'})}</S.BandConfidential>
			</S.Band>
			<S.PageContent>{children}</S.PageContent>
			<S.Foot>
				<span>{data.companyName}</span>
				<span>{intl.formatMessage({id: 'quote_designer.pdf.page_of'}, {page, total})}</span>
			</S.Foot>
		</S.PageSheet>
	);
};

const SectionHead = ({brand, children}: {brand: string; children: string}): JSX.Element => (
	<S.SectionLabelRow>
		<S.SectionLabelBar $brand={brand} />
		<S.SectionLabelText $brand={brand}>{children}</S.SectionLabelText>
	</S.SectionLabelRow>
);

const CoverBody = ({data, template}: PageProps): JSX.Element => {
	const intl = useIntl();
	const brand = template.brandColor;
	const logoSrc = data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const preparedLine = data.clientName
		? intl.formatMessage(
				{id: 'quote_designer.pdf.prepared'},
				{company: <strong>{data.companyName}</strong>, client: <strong>{data.clientName}</strong>}
		  )
		: intl.formatMessage({id: 'quote_designer.pdf.prepared_no_client'}, {company: <strong>{data.companyName}</strong>});

	const metaCells: Array<{label: string; value: string; brandColored?: boolean}> = [
		{label: intl.formatMessage({id: 'quote_designer.cover.quote_date'}), value: formatQuoteDate(data.issuedDate, intl)},
		{label: intl.formatMessage({id: 'quote_designer.cover.expiry_date'}), value: formatQuoteDate(data.expiryDate, intl)},
		{label: intl.formatMessage({id: 'quote_designer.cover.reference'}), value: data.projectIndicator, brandColored: true},
		{label: intl.formatMessage({id: 'quote_designer.cover.version'}), value: `v${data.revision}`},
	];

	return (
		<S.CoverContent>
			<div>
				{logoSrc ? (
					<S.CoverLogoImage src={logoSrc} crossOrigin="use-credentials" data-private alt="" />
				) : (
					<S.CoverLogoWord>{data.companyName}</S.CoverLogoWord>
				)}
			</div>
			<div>
				<S.CoverEyebrow $brand={brand}>{intl.formatMessage({id: 'quote_designer.pdf.eyebrow'})}</S.CoverEyebrow>
				<S.CoverTitle>{data.projectName}</S.CoverTitle>
				{data.clientName ? (
					<S.CoverFor>
						{intl.formatMessage({id: 'quote_designer.pdf.for_client'}, {client: data.clientName})}
					</S.CoverFor>
				) : null}
				<S.CoverPrepared>{preparedLine}</S.CoverPrepared>
			</div>
			<S.CoverMeta>
				{metaCells.map(cell => (
					<S.CoverMetaCell key={cell.label}>
						<S.CoverMetaLabel>{cell.label}</S.CoverMetaLabel>
						<S.CoverMetaValue $brand={cell.brandColored ? brand : undefined}>{cell.value}</S.CoverMetaValue>
					</S.CoverMetaCell>
				))}
			</S.CoverMeta>
		</S.CoverContent>
	);
};

const NarrativeBody = ({template}: {template: QuoteTemplate}): JSX.Element => {
	const intl = useIntl();
	return (
		<>
			{template.showIntroduction && (
				<S.Section>
					<SectionHead brand={template.brandColor}>
						{intl.formatMessage({id: 'quote_designer.section.introduction'})}
					</SectionHead>
					<PlateToHtml value={template.introduction} />
				</S.Section>
			)}
			{template.showScopeOfWork && (
				<S.Section>
					<SectionHead brand={template.brandColor}>
						{intl.formatMessage({id: 'quote_designer.section.scope'})}
					</SectionHead>
					<PlateToHtml value={template.scopeOfWork} />
				</S.Section>
			)}
		</>
	);
};

const TermsBody = ({template}: {template: QuoteTemplate}): JSX.Element => {
	const intl = useIntl();
	const terms = parseTermsBlocks(template.terms);
	return (
		<S.Section>
			<SectionHead brand={template.brandColor}>{intl.formatMessage({id: 'quote_designer.section.terms'})}</SectionHead>
			{terms.map((term, index) => (
				<S.TermRow key={index}>
					<S.TermNumber $brand={template.brandColor}>{index + 1}</S.TermNumber>
					<S.TermBody>
						{term.heading ? <S.TermHeading>{term.heading}</S.TermHeading> : null}
						{term.body ? <S.TermText>{term.body}</S.TermText> : null}
					</S.TermBody>
				</S.TermRow>
			))}
		</S.Section>
	);
};

const SignBlock = ({party, first, intl}: {party: string; first: boolean; intl: IntlShape}): JSX.Element => (
	<S.SignParty $first={first}>
		<S.SignOrg>{party}</S.SignOrg>
		<div>
			<S.SignSignatureSpace />
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.signature'})}</S.SignLabel>
		</div>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.full_name'})}</S.SignLabel>
		</S.SignField>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.signer_title'})}</S.SignLabel>
		</S.SignField>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'common.date'})}</S.SignLabel>
		</S.SignField>
	</S.SignParty>
);

const AgreementBody = ({data, template}: PageProps): JSX.Element => {
	const intl = useIntl();
	return (
		<S.Section>
			<SectionHead brand={template.brandColor}>
				{intl.formatMessage({id: 'quote_designer.section.agreement'})}
			</SectionHead>
			<S.Paragraph>
				{intl.formatMessage(
					{id: 'quote_designer.pdf.agreement_body'},
					{reference: data.projectIndicator, expiry: formatQuoteDate(data.expiryDate, intl)}
				)}
			</S.Paragraph>
			<S.SignGrid>
				<SignBlock party={data.companyName} first intl={intl} />
				<SignBlock party={data.clientName || data.projectName} first={false} intl={intl} />
			</S.SignGrid>
			<S.SignNote>{intl.formatMessage({id: 'quote_designer.pdf.digital_signature_note'})}</S.SignNote>
		</S.Section>
	);
};

/**
 * The live document preview: the same pages, conditions, and computed content as the PDF the
 * designer downloads or publishes, rendered as plain HTML so every settings change repaints in
 * place — no PDF regeneration, no viewer reload, and the scroll position survives edits.
 */
const QuotePreview: React.FC<PageProps> = ({data, template}) => {
	const pages: Array<{key: string; variant?: 'cover'; body: React.ReactNode}> = [
		{key: 'cover', variant: 'cover', body: <CoverBody data={data} template={template} />},
	];
	if (template.showIntroduction || template.showScopeOfWork) {
		pages.push({key: 'narrative', body: <NarrativeBody template={template} />});
	}
	pages.push({key: 'estimate', body: <QuotePreviewEstimate data={data} template={template} />});
	if (template.showTerms) {
		pages.push({key: 'terms', body: <TermsBody template={template} />});
	}
	pages.push({key: 'agreement', body: <AgreementBody data={data} template={template} />});

	return (
		<div data-cy="quote-preview">
			{pages.map((page, index) => (
				<PreviewPage
					key={page.key}
					data={data}
					template={template}
					variant={page.variant}
					page={index + 1}
					total={pages.length}
				>
					{page.body}
				</PreviewPage>
			))}
		</div>
	);
};

export default QuotePreview;
